import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  InMemoryCache,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { useAuth0 } from '@auth0/auth0-react'
import React from 'react'
import {
  academyHttpLink,
  academyErrorLink,
} from './Academy/AcademyApolloClient'
import { batchedLink, errorLink, httpLink } from './ApolloClient'
import {
  resiliaSearchHttpLink,
  resiliaSearchErrorLink,
} from './ResiliaSearch/ResiliaSearchApolloClient'
import {
  thirdPartyIntegrationLink,
  thirdPartyIntegrationErrorLink,
} from './ThirdPartyModule/ThirdPartyModule'

type Props = {
  children: React.ReactNode
}
const AuthorizedApolloProvider = ({ children }: Props) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0()

  const authMiddleware = setContext(async (_, { headers, ...context }) => {
    if (isAuthenticated) {
      const token = await getAccessTokenSilently()
      return {
        headers: {
          ...headers,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        ...context,
      }
    }
    return null
  })

  const link = ApolloLink.from([errorLink, authMiddleware, httpLink])
  const academyLink = ApolloLink.from([
    academyErrorLink,
    authMiddleware,
    academyHttpLink,
  ])
  const resiliaSearchLink = ApolloLink.from([
    resiliaSearchErrorLink,
    authMiddleware,
    resiliaSearchHttpLink,
  ])
  const thirdPartyLink = ApolloLink.from([
    thirdPartyIntegrationErrorLink,
    authMiddleware,
    thirdPartyIntegrationLink,
  ])
  const batchedLinkLink = ApolloLink.from([
    errorLink,
    authMiddleware,
    batchedLink,
  ])

  const addVariableMiddleware = new ApolloLink((operation, forward) => {
    if (localStorage.getItem('ImpersonationSession')) {
      operation.setContext(({ headers }: { headers: any }) => ({
        headers: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'X-Resilia-Impersonating': 'yes',
          ...headers,
        },
      }))
    }

    return forward(operation)
  })

  // ApolloLink.split can only perform a comparison, and it only has three possible cases, so we had to come up with a workaround
  const apolloClient = new ApolloClient({
    link: addVariableMiddleware.concat(
      ApolloLink.split(
        (operation) => operation.getContext().clientName === 'academy',
        academyLink,
        ApolloLink.split(
          (operation) => operation.getContext().clientName === 'thirdParty',
          thirdPartyLink,
          ApolloLink.split(
            (operation) =>
              operation.getContext().clientName === 'resiliaSearch',
            resiliaSearchLink,
            ApolloLink.split(
              (operation) => operation.getContext().clientName === 'single',
              link,
              batchedLinkLink
            )
          )
        )
      )
    ),
    cache: new InMemoryCache(),
    credentials: 'same-origin',
  })

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
}

export default AuthorizedApolloProvider
